import { MrbBaseListViewStore } from "mrb/core";
import { MrbTableViewStore } from "mrb/components";
import { MrbSelectStore } from "mrb/components/select";
import { makeObservable, action, observable, computed } from "mobx";
import { MrbFilterParameters } from "mrb/core/filter";
import { localizationService } from "common/localization";
import { map, indexOf, includes, forEach, some } from "lodash";
import { httpStatusCodes } from "mrb/core/constants";
import { applicationDefaults } from "common/constants";
import { dateFormatter, mrbDefaultFormats } from "mrb/common/formatting";
import { CertifierContentItem } from "application/certifier/components";

class CertifierContentListFilter extends MrbFilterParameters {
    @observable contentType;
    @observable certificationStatus;

    constructor() {
        super();
        makeObservable(this);
        this.reset();
    }

    reset() {
        super.reset();
        this.contentType = "station";
        this.certificationStatus = null;
    }
}

class CertifierContentListViewStore extends MrbBaseListViewStore {
    @observable selectedItems = [];
    selectedRowIds = [];

    @computed
    get selectedItemIds() {
        return map(this.selectedItems, (item) => item.id);
    }

    @computed
    get hasSelectedItems() {
        return this.selectedItems.length > 0;
    }

    @computed
    get hasApprovedInSelection() {
        return some(this.selectedItems, (item) => item.isApproved);
    }

    constructor(rootStore, { routeStore, dashboardViewStore }) {
        super(rootStore, {
            queryConfig: {
                filter: new CertifierContentListFilter(),
                orderBy: "dateAdded",
                orderDirection: "desc",
                pageSize: 10,
                disableUpdateQueryParams: true,
            },
        });
        makeObservable(this);
        this.routeStore = routeStore;
        this.dashboardViewStore = dashboardViewStore;

        this.statusFilterStore = new MrbSelectStore(
            {
                isSearchable: false,
                virtualized: false,
                isClearable: false,
                textKey: "label",
                valueKey: "value",
                placeholder: "APPLICATION.CERTIFIER.DASHBOARD.FILTER.STATUS_PLACEHOLDER",
                actions: {
                    onChange: async (option) => {
                        if (option) {
                            this.queryUtility.filter.set("certificationStatus", option.value);
                        } else {
                            this.queryUtility.filter.set("certificationStatus", "all");
                        }
                        await this.queryUtility.fetch();
                    },
                },
            },
            [
                { label: localizationService.t("APPLICATION.CERTIFIER.DASHBOARD.FILTER.ALL"), value: "all" },
                { label: localizationService.t("APPLICATION.CERTIFIER.DASHBOARD.STATUS.APPROVED"), value: "approved" },
                { label: localizationService.t("APPLICATION.CERTIFIER.DASHBOARD.STATUS.REJECTED"), value: "rejected" },
            ]
        );

        this.statusFilterStore.setSelectedItem({
            label: localizationService.t("APPLICATION.CERTIFIER.DASHBOARD.FILTER.ALL"),
            value: "all",
        });

        this.queryUtility.filter.orderBy = "dateAdded";
        this.queryUtility.filter.orderDirection = "desc";

        this.setGridStore(
            new MrbTableViewStore(this.rootStore, this.queryUtility, {
                columns: [
                    {
                        key: "selection",
                        title: "",
                        sortable: false,
                        headerCheckboxSelection: true,
                        checkboxSelection: true,
                        minWidth: 50,
                        maxWidth: 50,
                        resizable: false,
                        suppressMovable: true,
                        cell: () => null,
                    },
                    {
                        key: "name",
                        title: "APPLICATION.CERTIFIER.DASHBOARD.COLUMNS.TITLE",
                        sortable: true,
                        minWidth: 300,
                        flex: 1,
                        onCellClicked: (params) => {
                            if (params.data.stationId) {
                                this.rootStore.routerStore.goTo("master.administration.station.preview", {
                                    id: params.data.stationId,
                                });
                            } else if (params.data.podcastId) {
                                this.rootStore.routerStore.goTo("master.administration.podcast.preview", {
                                    id: params.data.podcastId,
                                });
                            } else if (params.data.nakiNigunAlbumId) {
                                this.rootStore.routerStore.goTo(
                                    "master.administration.naki-nigun.artist.preview",
                                    {
                                        id: params.data.artistId,
                                    },
                                    { albumId: params.data.nakiNigunAlbumId }
                                );
                            }
                        },
                    },
                    {
                        key: "contentType",
                        title: "APPLICATION.CERTIFIER.DASHBOARD.COLUMNS.TYPE",
                        sortable: false,
                        minWidth: 100,
                        maxWidth: 150,
                        format: (value) => {
                            if (!value) return "-";
                            return value.charAt(0).toUpperCase() + value.slice(1);
                        },
                    },
                    {
                        key: "producerName",
                        title: "APPLICATION.CERTIFIER.DASHBOARD.COLUMNS.PRODUCER",
                        sortable: true,
                        minWidth: 180,
                        flex: 0.5,
                        format: (value) => {
                            return value || "-";
                        },
                    },
                    {
                        key: "dateCreated",
                        title: "APPLICATION.CERTIFIER.DASHBOARD.COLUMNS.DATE_ADDED",
                        sortable: true,
                        minWidth: 120,
                        maxWidth: 180,
                        format: {
                            type: mrbDefaultFormats.date,
                            format: applicationDefaults.formats.date,
                        },
                    },
                    {
                        key: "dateCertified",
                        title: "APPLICATION.CERTIFIER.DASHBOARD.COLUMNS.DATE_CERTIFIED",
                        sortable: true,
                        minWidth: 120,
                        maxWidth: 180,
                        format: (value) => {
                            if (!value || value.startsWith("0001") || value.startsWith("1-01-01")) return "-";
                            return dateFormatter.format(value, {
                                format: applicationDefaults.formats.date,
                            });
                        },
                    },
                    {
                        key: "isApproved",
                        title: "APPLICATION.CERTIFIER.DASHBOARD.COLUMNS.STATUS",
                        sortable: false,
                        minWidth: 100,
                        maxWidth: 150,
                        cell: (props) => {
                            if (props.data && props.data.isPending)
                                return localizationService.t("APPLICATION.CERTIFIER.DASHBOARD.STATUS.PENDING");
                            if (props.data && props.data.isApproved)
                                return localizationService.t("APPLICATION.CERTIFIER.DASHBOARD.STATUS.APPROVED");
                            return localizationService.t("APPLICATION.CERTIFIER.DASHBOARD.STATUS.REJECTED");
                        },
                    },
                    {
                        key: "toggle",
                        title: "",
                        sortable: false,
                        minWidth: 80,
                        maxWidth: 80,
                        resizable: false,
                        cell: (props) => <CertifierContentItem item={props.data} onToggle={this.toggleItemApproval} />,
                    },
                ],
                selection: {
                    rowSelection: "multiple",
                    isRowSelectable: () => true,
                    onRowSelected: (node) => {
                        if (node.isSelected() && !includes(this.selectedItemIds, node.data.id)) {
                            this.selectedItems.push(node.data);
                        } else if (!node.isSelected() && includes(this.selectedItemIds, node.data.id)) {
                            const index = indexOf(this.selectedItemIds, node.data.id);
                            if (index !== -1) {
                                this.selectedItems.splice(index, 1);
                            }
                        }
                    },
                },
                onDataChange: () => {
                    if (this.gridStore.gridApi) {
                        const allRows = this.gridStore.gridApi.getModel().rowsToDisplay;
                        const rowsToSelect = allRows.filter((row) => this.selectedItemIds.includes(row.data.id));
                        if (rowsToSelect.length > 0) {
                            forEach(rowsToSelect, (row) => {
                                row.setSelected(true);
                            });
                        }
                    }
                },
            })
        );

        this.reaction(
            () => ({
                initialized: this.gridStore && this.gridStore.initialized,
                contentType: this.queryUtility.filter.contentType,
            }),
            ({ initialized, contentType }) => {
                if (initialized && this.gridStore && this.gridStore.gridColumnApi) {
                    this.gridStore.gridColumnApi.setColumnVisible("producerName", contentType !== "station");
                }
            },
            { fireImmediately: true }
        );
    }

    async find(params) {
        const modeId = this.dashboardViewStore.selectedModeId;
        if (!modeId) {
            return { item: [], totalRecords: 0 };
        }
        return this.routeStore.findContent(modeId, params);
    }

    onFetchError(err) {
        if (err && err.statusCode === httpStatusCodes.CanceledRequest) {
            return;
        }
        this.rootStore.notificationStore.error(
            localizationService.t("APPLICATION.CERTIFIER.DASHBOARD.NOTIFICATIONS.LOAD_ERROR"),
            err
        );
    }

    @action.bound
    async toggleItemApproval(item, value) {
        const isRejectingApproved = !value && item.isApproved;

        const performToggle = async () => {
            const oldApproved = item.isApproved;
            const oldPending = item.isPending;
            const oldDateCertified = item.dateCertified;

            item.isApproved = value;
            item.isPending = false;
            if (value) {
                item.dateCertified = new Date().toISOString();
            } else {
                item.dateCertified = null;
            }

            try {
                await this.routeStore.updateContentStatus(
                    this.dashboardViewStore.selectedModeId,
                    item.contentId,
                    value,
                    item.contentType
                );
                await this.dashboardViewStore.fetchSummary();
                await this.queryUtility.fetch();
            } catch (err) {
                item.isApproved = oldApproved;
                item.isPending = oldPending;
                item.dateCertified = oldDateCertified;
                this.rootStore.notificationStore.error(
                    localizationService.t("APPLICATION.CERTIFIER.DASHBOARD.NOTIFICATIONS.STATUS_UPDATE_ERROR"),
                    err
                );
            }
        };

        if (isRejectingApproved) {
            this.rootStore.confirmDialogStore.showConfirm({
                message: localizationService.t("APPLICATION.CERTIFIER.DASHBOARD.CONFIRM_REJECT"),
                onConfirm: performToggle,
            });
        } else {
            await performToggle();
        }
    }

    @action.bound
    setContentTypeFilter(contentType) {
        this.queryUtility.filter.set("contentType", contentType);
        this.clearSelection();
        this.queryUtility.fetch();
    }

    @action.bound
    clearSelection() {
        this.selectedItems = [];
        if (this.gridStore.gridApi) {
            this.gridStore.gridApi.deselectAll();
        }
    }

    @action.bound
    async bulkApprove() {
        if (this.selectedItems.length === 0) return;

        const contentType = this.queryUtility.filter.contentType;
        const resources = map(this.selectedItems, (item) => ({
            contentId: item.contentId,
            contentType: contentType,
            isApproved: true,
        }));
        try {
            await this.routeStore.bulkUpdateContentStatus(this.dashboardViewStore.selectedModeId, resources);
            await this.dashboardViewStore.fetchSummary();
            this.clearSelection();
            await this.queryUtility.fetch();
        } catch (err) {
            this.rootStore.notificationStore.error(
                localizationService.t("APPLICATION.CERTIFIER.DASHBOARD.NOTIFICATIONS.BULK_STATUS_ERROR"),
                err
            );
        }
    }

    @action.bound
    bulkReject() {
        if (this.selectedItems.length === 0) return;

        const contentType = this.queryUtility.filter.contentType;
        const hasApproved = this.hasApprovedInSelection;
        const performReject = async () => {
            const resources = map(this.selectedItems, (item) => ({
                contentId: item.contentId,
                contentType: contentType,
                isApproved: false,
            }));
            try {
                await this.routeStore.bulkUpdateContentStatus(this.dashboardViewStore.selectedModeId, resources);
                await this.dashboardViewStore.fetchSummary();
                this.clearSelection();
                await this.queryUtility.fetch();
            } catch (err) {
                this.rootStore.notificationStore.error(
                    localizationService.t("APPLICATION.CERTIFIER.DASHBOARD.NOTIFICATIONS.BULK_STATUS_ERROR"),
                    err
                );
            }
        };

        if (hasApproved) {
            this.rootStore.confirmDialogStore.showConfirm({
                message: localizationService.t("APPLICATION.CERTIFIER.DASHBOARD.CONFIRM_BULK_REJECT"),
                onConfirm: performReject,
                yesLabel: "Yes",
                noLabel: "No",
            });
        } else {
            performReject();
        }
    }
}

export default CertifierContentListViewStore;
